import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@root/src/environments/environment';
import { AuthService } from './auth.service';

export interface TileMapDataResponse {
    regionId: string;
    data?: any;
    color?: string;
    message?: string;
}

export interface WeatherTileMapBatchApiRequest {
     regionMapIds: string[];
     dataType: number;
     /** Data source: 0 = Era, 1 = IBM */
     dataSource?: number;
    startDate?: string;
    endDate?: string;
    date?: string;
    cropId?: string;
}

export interface WeatherTileMapBatchApiResponse {
    regionMapId: string;
    color: string;
    data?: any;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class TileMapDataService {
    private readonly batchApiUrl = `${environment.api}/weatherReports/WeatherTileMap/GetTileMapDataBatch`;

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) {}

    /**
     * Получение данных для нескольких регионов одним запросом
     */
    getTileMapDataBatch(request: WeatherTileMapBatchApiRequest): Observable<TileMapDataResponse[]> {
        return this.callRealBatchApi(request).pipe(
            map((responses) => {
                const results: TileMapDataResponse[] = responses.map(response => ({
                    regionId: response.regionMapId,
                    data: response.data,
                    color: (response.color as 'green' | 'yellow' | 'red' | 'blue' | 'gray') || 'gray',
                    message: response.message
                }));
                console.log(`✅ Batch data loaded for ${results.length} regions`);
                return results;
            }),
            catchError(error => {
                console.error(`❌ Error fetching batch tile map data, dataType ${request.dataType}:`, error);
                const fallback: TileMapDataResponse[] = request.regionMapIds.map(id => ({
                    regionId: id,
                    data: null,
                    color: 'gray',
                    message: `Error loading data: ${error?.message || 'unknown'}`
                }));
                return of(fallback);
            })
        );
    }

    /**
     * Вызов реального API для получения batch данных плиточной карты погоды
     */
    private callRealBatchApi(request: WeatherTileMapBatchApiRequest): Observable<WeatherTileMapBatchApiResponse[]> {
        const token = this.authService.getToken();
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        });

        const apiRequest: WeatherTileMapBatchApiRequest = {
            regionMapIds: request.regionMapIds,
            dataType: request.dataType,
            dataSource: request.dataSource ?? 0,
            startDate: request.startDate ? new Date(request.startDate).toISOString() : undefined,
            endDate: request.endDate ? new Date(request.endDate).toISOString() : undefined,
            date: request.date ? new Date(request.date).toISOString() : undefined,
            cropId: request.cropId ? String(request.cropId) : undefined
        };

        console.log(`🌐 Batch API Request to ${this.batchApiUrl}:`, {
            ...apiRequest,
            regionMapIdsCount: apiRequest.regionMapIds.length
        });

        return this.http.post<WeatherTileMapBatchApiResponse[]>(this.batchApiUrl, apiRequest, { headers }).pipe(
            map((response) => {
                console.log(`✅ Batch API Response: ${response.length} regions, dataType ${request.dataType}`);
                return response;
            }),
            catchError((error) => {
                console.error(`❌ Batch API Error, dataType ${request.dataType}:`, error);
                throw error;
            })
        );
    }

}
